import { EBeneficiaryType, ECreditApplicationStatus, EDecision, EFinancingProgram } from '@if/core/types'
import { useUser } from '@src/contexts/AppContext'
import { useCreditApplicationById, useEditCreditApplication } from '@src/data/api/credit-api/credit-api'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import useFeatureFlags from '@src/hooks/config-hooks'
import { DraftCreditApplicationDto } from '@src/types/CreditApplicationSchema'
import type { JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  adjustCreditApplicationYearsForApi,
  adjustCreditApplicationYearsFromApi,
  mustAskApplicantSIN,
  mustAskCoapplicantSIN,
} from '../ViewCreditApplicationPage/credit-hooks'
import CreditApplicationForm from './components/CreditApplicationForm'

interface Props {
  id: string
  financingProgramId: EFinancingProgram
}

const EditCreditApplicationPage = ({ id, financingProgramId }: Props): JSX.Element => {
  const user = useUser()
  const [merchant] = useMerchantById(user?.merchantId ?? '')
  const navigate = useNavigate()

  const { isFetchingFeatureSwitch } = useFeatureFlags()

  const [creditApplication, isLoadingCreditApp] = useCreditApplicationById(
    {
      creditApplicationId: id,
      financingProgramId,
    },
    false,
  ) // if true, poll every 5 secs

  const shouldShowSimplifiedApplicantEditDialog =
    creditApplication?.applicant?.incomeVerification?.allIncomeTasksConfirmed &&
    !creditApplication?.applicant?.incomeVerification?.hasSkippedIncomeValidation
  const shouldShowSimplifiedCoApplicantEditDialog =
    creditApplication?.coApplicant?.incomeVerification?.allIncomeTasksConfirmed &&
    !creditApplication?.coApplicant?.incomeVerification?.hasSkippedIncomeValidation

  const [editCreditAppMutation, isPending, isError, reset, error] = useEditCreditApplication()
  const onSave = async (data: Partial<DraftCreditApplicationDto>) => {
    data.id = id
    if (!data.beneficiaryTypeId) data.beneficiaryTypeId = EBeneficiaryType.Applicant
    adjustCreditApplicationYearsForApi(data)
    await editCreditAppMutation(data).then(() => {
      reset()
      if (data.status === ECreditApplicationStatus.Draft) navigate(`/`)
      else {
        navigate(`/credits/${data.financingProgramId}/${data.id}`)
      }
    })
  }
  const isPrequalComplete =
    creditApplication?.prequalificationDecision?.decision === EDecision.Approved ||
    (creditApplication?.prequalificationDecision?.decision === EDecision.Refused &&
      creditApplication?.applicant?.hasCreditReport === true)

  return (
    <div>
      {!isLoadingCreditApp && creditApplication && !isFetchingFeatureSwitch && (
        <CreditApplicationForm
          merchant={merchant}
          creditApplication={adjustCreditApplicationYearsFromApi(creditApplication)}
          onSave={onSave}
          isPending={isPending}
          isError={isError}
          serverErrors={error}
          financingProgramId={financingProgramId}
          lockApplicantIncomeAndExpenses={shouldShowSimplifiedApplicantEditDialog}
          lockCoApplicantIncomeAndExpenses={shouldShowSimplifiedCoApplicantEditDialog}
          isPrequalComplete={isPrequalComplete}
          isApplicantSinNeeded={mustAskApplicantSIN(creditApplication)}
          isCoapplicantSinNeeded={mustAskCoapplicantSIN(creditApplication)}
        />
      )}
    </div>
  )
}

export default EditCreditApplicationPage
